// src/pages/events/EventList.js
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/AdminHeader';
import LayoutContainer from '../../components/LayoutContainer';
import eventService from '../../services/eventService';
import moment from 'moment';
import { find, reverse, sortBy } from 'lodash';
import { IoMdCheckboxOutline } from 'react-icons/io';
import { MdOutlineCheckBoxOutlineBlank } from 'react-icons/md';
import { RxCrossCircled } from 'react-icons/rx';
import MUIDataTable from 'mui-datatables';
import { AiOutlineProduct } from 'react-icons/ai';
import ProductByEvent from '../items/ProductByEvent';

const EventList = () => {
  const [events, setEvents] = useState([]);
  const [id, setId] = useState('');
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      const response = await eventService.getEvents();
      setEvents(reverse(sortBy(response.data, 'event_date')));
    };
    fetchEvents();
  }, []);

  const handleDelete = async (id) => {
    await eventService.deleteEvent(id);
    setEvents(events.filter((event) => event.id !== id));
  };

  useEffect(() => {
    const activeEvent = find(events, { active: true })?.id;
    if (activeEvent) {
      localStorage?.setItem('event_id', activeEvent);
    }
  }, [events]);

  const columns = [
    {
      name: 'id',
      label: 'Id',
      options: {
        filter: false,
        sort: true,
        display: false,
        customBodyRender: (value) => value,
      },
    },
    {
      name: 'name',
      label: 'EVENT',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value,
      },
    },
    {
      name: 'event_date',
      label: 'DATE',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => moment(value).format('DD/MM/YYYY'),
      },
    },
    {
      name: 'city',
      label: 'CITY',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'active',
      label: 'ACTIVE',
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value) =>
          value ? (
            <IoMdCheckboxOutline fill='green' size={22} />
          ) : (
            <RxCrossCircled fill='red' size={22} style={{ color: 'red' }} />
          ),
      },
    },
    {
      name: 'photo',
      label: 'LOGO',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) =>
          value ? (
            <img
              src={`${process.env.REACT_APP_IMAGE_URL}/images/${value}`}
              height={45}
              width={50}
              style={{ borderRadius: '50%' }}
              alt='Event Logo'
            />
          ) : (
            ''
          ),
      },
    },
    {
      name: 'kml',
      label: 'GEOJSON',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) =>
          value ? (
            <a
              href={`${process.env.REACT_APP_IMAGE_URL}/mapfiles/${value}`}
              target='_blank'
              rel='noopener noreferrer'
              style={{ textDecoration: 'none' }}
            >
              {value}
            </a>
          ) : (
            ''
          ),
      },
    },
    {
      name: 'action',
      label: 'ACTION',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta, updateValue) => (
          <div>
            <IconButton
              className='iconButton'
              onClick={() => navigate(`/events/edit/${tableMeta.rowData[0]}`)}
            >
              <EditIcon sx={{ color: '#be210b' }} />
            </IconButton>
            <IconButton
              onClick={() => {
                setId(tableMeta?.rowData[0]);
                setOpenModal(true);
              }}
            >
              <AiOutlineProduct color='#0887D0' />
            </IconButton>
          </div>
        ),
      },
    },
  ];

  const options = {
    expandableRows: false,
    download: false,
    filterOptions: 'fullWidth',
    selectableRows: 'none',
    print: false,
    rowsPerPageOptions: [30, 50, 100],
    rowsPerPage: 30,
    viewColumns: false,
    downloadOptions: {
      filterOptions: {
        useDisplayedColumnsOnly: true,
        useDisplayedRowsOnly: true,
      },
    },
  };

  const getMuiTheme = () =>
    createTheme({
      components: {
        MUIDataTable: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              backgroundColor: '#FFF',
            },
          },
        },
        MUIDataTableHeadCell: {
          styleOverrides: {
            root: {
              backgroundColor: '#2F3542', // Keep background as you have it
              color: '#fff !important', // Ensure heading text is white
              border: 'none',
              fontWeight: '550 !important',
              '&:nth-last-of-type(1)': {
                borderTopRightRadius: '1rem',
                // borderBottomRightRadius: '0.2rem',
              },
            },
            sortActive: {
              color: '#fff !important', // Active sort icon color should also be white
            },
            sortAction: {
              '& .MuiTableSortLabel-icon': {
                color: '#fff !important', // Ensure sort icon stays white when active
              },
            },
            toolButton: {
              fontWeight: 'bold',
              fontSize: '0.675rem !important',
              paddingTop: '3px',
              paddingBottom: '3px',
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              padding: '2px 8px 2px 8px',
            },
          },
        },
        MuiTablePagination: {
          styleOverrides: {
            root: {
              backgroundColor: '#FFFFFF',
              height: '40px',
            },
            select: {
              backgroundColor: '#FFFFFF',
            },
            actions: {
              backgroundColor: '#FFFFFF',
            },
          },
        },
        MUIDataTableSelectCell: {
          styleOverrides: {
            headerCell: {
              border: 'none',
              borderTopLeftRadius: '0.2rem',
              borderBottomLeftRadius: '0.2rem',
              backgroundColor: '#2F3542',
              '& .MuiCheckbox-root': {
                color: '#fff',
                padding: '4px',
              },
            },
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: {
              minHeight: '30px!important',
              padding: '0px!important',
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              padding: '4px',
            },
          },
        },
        MUIDataTableBodyRow: {
          styleOverrides: {
            root: {
              '&:nth-of-type(even)': {
                backgroundColor: '#EAF3FF',
              },
              '&:nth-of-type(odd)': {
                backgroundColor: '#FFF',
              },
              '& td': {
                textAlign: 'left', // Center align body text data
                border: 'none',
              },
              marginBottom: '10px',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              color: '#fff',
              padding: '6px 4px !important',
              fontWeight: '550 !important',
              fontSize: '0.675rem !important',
              justifyContent: 'start !important',
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              padding: '2px 12px !important',
              fontSize: '0.675rem',
            },
          },
        },
        MUIDataTableBodyCell: {
          styleOverrides: {
            root: {
              fontSize: '0.675rem !important',
            },
          },
        },
        MuiPopover: {
          styleOverrides: {
            paper: {
              minWidth: '500px !important', // Increase the width of filter dropdown
              padding: '10px', // Add padding inside the dropdown
              borderRadius: '8px', // Optionally, round the corners
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)', // Add a subtle shadow for better visibility
              backgroundColor: '#fff', // Background color (you can change it)
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              minWidth: '200px  !important',
              maxHeight: '200px',
              overflowY: 'auto',
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: '0.675rem !important',
              fontWeight: 500,
              color: '#333',
              padding: '8px 16px',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            select: {
              fontSize: '0.675rem !important',
              padding: '6px 10px',
            },
          },
        },
      },
    });

  return (
    <LayoutContainer>
      <AdminHeader title='EVENTS' />
      <div className='page-content-wrapper scrollable'>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant='contained'
            sx={{ backgroundColor: '#be210b', textTransform: 'none' }}
            onClick={() => navigate('/events/new')}
          >
            ADD EVENT
          </Button>
        </Box>
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable data={events} columns={columns} options={options} />
        </ThemeProvider>

        {/* <Table className='table'>
          <TableHead className='tableHead'>
            <TableRow>
              <TableCell>Event</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Logo</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody className='tableBody'>
            {events.map((event, index) => (
              <TableRow
                key={event?.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? '#fff' : '#eaf3ff',
                }}
              >
                <TableCell>{event?.name}</TableCell>
                <TableCell>
                  {moment(event.date)?.format('DD/MM/YYYY')}
                </TableCell>
                <TableCell>{event?.city}</TableCell>
                <TableCell>
                  {event?.active ? (
                    <IoMdCheckboxOutline fill='green' size={22}/>
                  ) : (
                    <RxCrossCircled  fill='red' size={22} style={{color:'red'}}/>
                  )}
                </TableCell>
                <TableCell>
                  {
                    <img
                      src={event?.photo}
                      height={45}
                      width={50}
                      style={{ borderRadius: '100%' }}
                      alt={event?.name}
                    />
                  }
                </TableCell>
                <TableCell>
                  <IconButton
                    className='iconButton'
                    onClick={() => navigate(`/events/edit/${event.id}`)}
                  >
                    <EditIcon sx={{ color: '#be210b' }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table> */}
      </div>
      <ProductByEvent open={openModal} onClose={() => setOpenModal(false)} id={id}/>
    </LayoutContainer>
  );
};

export default EventList;
